// src/pages/TimeSheetAlert.tsx
import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/firebase";
import CustomDropdown from "../components/CustomDropdown";
import {
  eachDayOfInterval,
  isSunday,
  parseISO,
  isAfter,
  isBefore,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  format,
} from "date-fns";
import { ArrowLeft, Calendar as CalendarIcon, Table, RefreshCw } from "lucide-react";
import { useColorMode, Skeleton } from "@chakra-ui/react";
import { getThemeClasses } from "../utils/themeUtils";
import TimeSheetTable from "./TimeSheetTable";
import { fetchLeaveEntriesForUsers } from "../utils/leaveUtils";

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  joiningDate?: string; // ISO yyyy-MM-dd
}

interface TimeSheetAlertProps {
  organizationId: string;
  users: User[];
  onChangeMode?: (mode: "CheckIn" | "TimeSheets") => void;
  currentUser?: any;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Global memory cache to prevent redundant Firestore fetches on tab toggle/re-render
const timesheetCache: {
  key: string | null;
  data: {
    missedCount: Record<string, number>;
    userTimes: Record<string, Date[]>;
  } | null;
  timestamp: number;
} = {
  key: null,
  data: null,
  timestamp: 0,
};

const TimeSheetAlert: React.FC<TimeSheetAlertProps> = ({
  organizationId,
  users,
  onChangeMode,
  currentUser,
}) => {
  const now = new Date();
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  // View mode state
  const [viewMode, setViewMode] = useState<"CheckIn" | "TimeSheets">(
    "TimeSheets"
  );
  const [isTableMode, setIsTableMode] = useState(false);

  // Add calendar styles
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .big-calendar {
        width: 100% !important;
        font-size: 1.1em !important;
        border: none !important;
        background: ${isDark ? '#1A1C1F' : 'white'} !important;
      }
      .big-calendar .react-calendar__navigation {
        background: ${isDark ? '#14161B' : '#f8fafc'} !important;
        border-bottom: 1px solid ${isDark ? '#2E2F33' : '#e2e8f0'} !important;
        margin-bottom: 0 !important;
        padding: 1rem !important;
      }
      .big-calendar .react-calendar__navigation button {
        color: ${isDark ? '#C3C9D3' : '#4a5568'} !important;
        font-weight: 600 !important;
        background: none !important;
        border: none !important;
        font-size: 1.1em !important;
      }
      .big-calendar .react-calendar__navigation button:hover {
        background: ${isDark ? '#2A3441' : '#e2e8f0'} !important;
        border-radius: 0.5rem !important;
      }
      .big-calendar .react-calendar__month-view__weekdays {
        background: ${isDark ? '#1A1C1F' : '#f1f5f9'} !important;
        font-weight: 600 !important;
        color: ${isDark ? '#C3C9D3' : '#64748b'} !important;
        font-size: 0.875rem !important;
        text-transform: uppercase !important;
        letter-spacing: 0.05em !important;
        padding: 0.75rem 0 !important;
        border-bottom: 1px solid ${isDark ? '#2E2F33' : '#e2e8f0'} !important;
      }
      .big-calendar .react-calendar__month-view__weekdays__weekday {
        padding: 0.5rem !important;
        text-align: center !important;
      }
      .big-calendar .react-calendar__month-view__days {
        background: ${isDark ? '#1A1C1F' : 'white'} !important;
      }
      .big-calendar .react-calendar__tile {
        position: relative !important;
        background: ${isDark ? '#14161B' : 'white'} !important;
        border: 1px solid ${isDark ? '#2E2F33' : '#f1f5f9'} !important;
        color: ${isDark ? '#FFFFFF' : '#374151'} !important;
        font-weight: 500 !important;
        height: 3.5rem !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        transition: all 0.2s ease !important;
      }
      .big-calendar .react-calendar__tile:hover {
        background: ${isDark ? '#2A3441' : '#f8fafc'} !important;
        transform: translateY(-1px) !important;
        box-shadow: ${isDark ? 'none' : '0 2px 4px rgba(0,0,0,0.1)'} !important;
      }
      .big-calendar .react-calendar__tile--now {
        background: ${isDark ? '#3A4553' : '#fef3c7'} !important;
        color: ${isDark ? '#FBD282' : '#eab308'} !important;
        font-weight: 700 !important;
        border: 2px solid ${isDark ? '#B59143' : '#f59e0b'} !important;
      }
      .big-calendar .react-calendar__month-view__days__day--sunday {
        color: #dc2626 !important;
        background: ${isDark ? '#2A3441' : '#fef2f2'} !important;
        border-color: ${isDark ? '#7f1d1d' : '#fecaca'} !important;
      }
      .big-calendar .react-calendar__month-view__days__day--weekend:not(.react-calendar__month-view__days__day--sunday) {
        color: #374151 !important;
      }
      .big-calendar .react-calendar__tile--active {
        background: ${isDark ? '#2A3441' : '#FFF7E6'} !important;
        color: ${isDark ? '#FBD282' : '#B59143'} !important;
        border-color: ${isDark ? '#B59143' : '#E8C16C'} !important;
      }
      .big-calendar .react-calendar__tile--active:hover {
        background: ${isDark ? '#3A4553' : '#FAEDD0'} !important;
      }
      .year-calendar {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1rem;
        max-width: none;
      }
      @media (max-width: 1024px) {
        .year-calendar {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      @media (max-width: 640px) {
        .year-calendar {
          grid-template-columns: 1fr;
        }
      }
      .month-calendar {
        background: ${isDark ? '#1A1C1F' : 'white'};
        border-radius: 0.5rem;
        box-shadow: ${isDark ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)'};
        overflow: hidden;
        border: 1px solid ${isDark ? '#2E2F33' : '#e2e8f0'};
      }
      .month-calendar.current-month {
        border: 1px solid #22c55e;
        box-shadow: 0 1px 3px rgba(34, 197, 94, 0.15);
      }
      .month-calendar-header {
        background: ${isDark ? '#14161B' : '#f8fafc'};
        padding: 0.5rem;
        border-bottom: 1px solid ${isDark ? '#2E2F33' : '#e2e8f0'};
        text-align: center;
        font-weight: 700;
        color: ${isDark ? '#FFFFFF' : '#374151'};
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      .month-calendar .react-calendar {
        width: 100% !important;
        border: none !important;
        font-size: 0.8rem !important;
      }
      .month-calendar .react-calendar__navigation {
        display: none !important;
      }
      .month-calendar .react-calendar__tile {
        height: 2rem !important;
        font-size: 0.9rem !important;
        color: ${isDark ? '#FFFFFF' : '#374151'} !important;
        font-weight: 500 !important;
        border: 1px solid ${isDark ? '#2E2F33' : '#f1f5f9'} !important;
        background: ${isDark ? '#14161B' : 'white'} !important;
        position: relative !important;
      }
      .month-calendar .react-calendar__month-view__weekdays {
        font-size: 0.75rem !important;
        padding: 0.5rem 0 !important;
        color: #64748b !important;
        font-weight: 600 !important;
      }
      .month-calendar .react-calendar__tile abbr {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: auto !important;
        height: auto !important;
        border-radius: 0 !important;
        background: transparent !important;
        transition: color 0.15s ease !important;
      }
      .month-calendar .react-calendar__tile--missed abbr {
        background: transparent !important;
        color: #dc2626 !important; /* red-600 */
      }
      .month-calendar .react-calendar__tile--submitted abbr {
        background: transparent !important;
        color: #16a34a !important; /* green-600 */
        font-weight: 700 !important;
      }
      .month-calendar .react-calendar__tile--sundaysubmitted abbr {
        background: transparent !important;
        color: #eab308 !important; /* yellow-500 */
        border: none !important;
        font-weight: 700 !important;
      }
      .month-calendar .react-calendar__tile--holidaysubmitted abbr {
        background: transparent !important;
        color: #eab308 !important; /* amber-700 */
        border: none !important;
        font-weight: 700 !important;
      }
      .month-calendar .react-calendar__tile--holiday abbr {
        background: ${isDark ? "#B59143" : "#3b82f6"} !important;
        color: white !important;
        border: none !important;
        border-radius: 50% !important;
        font-weight: 600 !important;
        padding: 0 !important;
        width: 24px !important;
        height: 24px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-size: 0.8em !important;
      }

    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // dropdown‐driven month and year
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // Default to current month
  const [selectedYear, setSelectedYear] = useState(now.getFullYear()); // Default to current year

  // user → missed‐days count
  const [missedCount, setMissedCount] = useState<Record<string, number>>({});

  // user → all timesheet submission dates
  const [userTimes, setUserTimes] = useState<Record<string, Date[]>>({});

  // holidays data
  const [holidays, setHolidays] = useState<Record<string, any>>({});
  // approved leave data for each user
  const [userApprovedLeaves, setUserApprovedLeaves] = useState<Record<string, any[]>>({});

  const [loading, setLoading] = useState(false);

  // which user's calendar we're viewing
  const [calendarUserId, setCalendarUserId] = useState<string | null>(null);

  // Calendar year (defaults to current year)
  const [calendarYear, setCalendarYear] = useState(now.getFullYear());

  // Search state
  const [searchQuery, setSearchQuery] = useState("");



  // drives the Calendar's displayed month/year
  const [activeDate, setActiveDate] = useState<Date>(
    startOfMonth(new Date(selectedYear, selectedMonth - 1))
  );

  // Generate year options (2025 to current year only)
  const yearOptions = [];
  const currentYear = now.getFullYear();
  for (let year = 2025; year <= currentYear; year++) {
    yearOptions.push(year);
  }

  // fetch data on mount or month/year change
  const fetchData = async (forceRefresh = false) => {
    const year = calendarUserId ? calendarYear : selectedYear;
    const cacheKey = `${organizationId}-${year}-${selectedMonth}-${users.map((u) => u.id).join(",")}`;
    const cacheAge = Date.now() - timesheetCache.timestamp;

    if (!forceRefresh && timesheetCache.key === cacheKey && timesheetCache.data && cacheAge < 10 * 60 * 1000) {
      setMissedCount(timesheetCache.data.missedCount);
      setUserTimes(timesheetCache.data.userTimes);
      setLoading(false);
      return;
    }

    setLoading(true);

    // 1. Fetch holidays if not loaded or forceRefresh
    let activeHolidays = holidays;
    if (Object.keys(activeHolidays).length === 0 || forceRefresh) {
      try {
        const holidaysCol = collection(db, "organization", organizationId, "holidays");
        const holidaysSnap = await getDocs(holidaysCol);
        const holidaysData: Record<string, any> = {};
        holidaysSnap.forEach((doc) => {
          const data = doc.data();
          holidaysData[doc.id] = { id: doc.id, date: data.date, reason: data.reason };
        });
        setHolidays(holidaysData);
        activeHolidays = holidaysData;
      } catch (error) {
        console.error("❌ Error fetching holidays:", error);
      }
    }

    // 2. Fetch approved leaves if not loaded or forceRefresh
    let activeLeaves = userApprovedLeaves;
    if (Object.keys(activeLeaves).length === 0 || forceRefresh) {
      try {
        const allEntriesByUser = await fetchLeaveEntriesForUsers(
          organizationId,
          users.map(u => u.id)
        );

        const approvedLeavesData: Record<string, any[]> = {};
        Object.entries(allEntriesByUser).forEach(([uid, entries]) => {
          approvedLeavesData[uid] = entries.filter((e: any) =>
            e.approvalStatus === "Approved" || e.approvalStatus === "approved"
          );
        });

        setUserApprovedLeaves(approvedLeavesData);
        activeLeaves = approvedLeavesData;
      } catch (error) {
        console.error("❌ Error fetching approved leaves:", error);
      }
    }


    let start: Date;
    let end: Date;

    // If no month selected (month = 0), calculate for entire year up to present
    if (selectedMonth === 0) {
      start = startOfYear(new Date(year, 0));
      end = year === now.getFullYear() ? now : endOfYear(start);
    } else {
      const mIdx = selectedMonth - 1;
      start = startOfMonth(new Date(year, mIdx));
      end =
        mIdx === now.getMonth() && year === now.getFullYear()
          ? now
          : endOfMonth(start);
    }



    const counts: Record<string, number> = {};
    const timesMap: Record<string, Date[]> = {};

    for (const u of users) {
      try {
        // Determine user-specific business day keys in this month range starting from joining date
        let userStart = start;
        if (u.joiningDate) {
          try {
            const jd = parseISO(u.joiningDate);
            if (!isNaN(jd.getTime())) {
              // If user joined after the period end, they have 0 missed days for this period
              if (isAfter(jd, end)) {
                counts[u.id] = 0;
                timesMap[u.id] = [];
                continue;
              }
              // If user joined during this period, start counting from joining date
              if (isAfter(jd, userStart)) {
                userStart = jd;
              }
            }
          } catch { }
        }
        const userBizKeys = eachDayOfInterval({ start: userStart, end })
          .filter((d) => {
            const dateKey = format(d, "yyyy-MM-dd");
            return !isSunday(d) && !activeHolidays[dateKey];
          })
          .map((d) => format(d, "yyyy-MM-dd"));

        // READ: organization/{orgId}/TimeSheetsData/{userId}/weeks
        const entries: any[] = [];
        const seenKeys = new Set<string>();

        // 1. Weekly architecture
        const weekStartStr = `${year - 1}-12-20`;
        const weekEndStr = `${year + 1}-01-10`;

        const tsColRef = collection(db, "organization", organizationId, "TimeSheetsData", u.id, "weeks");
        const q = query(
          tsColRef,
          where("weekMonday", ">=", weekStartStr),
          where("weekMonday", "<=", weekEndStr)
        );
        const userDocSnap = await getDocs(q);
        userDocSnap.docs.forEach(wkDoc => {
          const wkData = wkDoc.data();
          const weekEntries = wkData.entries || [];
          weekEntries.forEach((entry: any) => {
            entries.push(entry);
            const key = `${entry.date}-${entry.project}-${entry.hours}-${entry.timestamp}`;
            seenKeys.add(key);
          });
        });

        // 2. Legacy path
        const oldRef = doc(db, "organization", organizationId, "timesheets", u.id);
        const oldSnap = await getDoc(oldRef);
        if (oldSnap.exists()) {
          const legacyEntries = oldSnap.data().entries || [];
          legacyEntries.forEach((entry: any) => {
            const key = `${entry.date}-${entry.project}-${entry.hours}-${entry.timestamp}`;
            if (!seenKeys.has(key)) {
              entries.push(entry);
            }
          });
        }

        // Normalize to date keys (yyyy-MM-dd) – resilient to TZ drift
        const submittedKeySet = new Set<string>();
        const parsedDates: Date[] = [];

        for (const e of entries) {
          const rawDate: string | undefined = e?.date; // expected "YYYY-MM-DD"
          if (!rawDate || typeof rawDate !== "string") continue;

          // Keep as YYYY-MM-DD for key; also keep a Date for existing UI
          submittedKeySet.add(rawDate);
          try {
            parsedDates.push(parseISO(rawDate));
          } catch {
            // ignore parse failures; key already captured
          }
        }



        // Limit to the selected period
        const inPeriodSubmittedCount = userBizKeys.filter((k) =>
          submittedKeySet.has(k)
        ).length;

        // Subtract approved leave days within this period
        const userLeaves = activeLeaves[u.id] || [];
        const leaveDaysInPeriod = new Set<string>();
        userLeaves.forEach((leave: any) => {
          const fromDate = new Date(leave.fromDate);
          const toDate = new Date(leave.toDate);
          const leaveDates = eachDayOfInterval({ start: fromDate, end: toDate });
          leaveDates.forEach((leaveDate) => {
            const key = format(leaveDate, "yyyy-MM-dd");
            if (userBizKeys.includes(key)) leaveDaysInPeriod.add(key);
          });
        });

        counts[u.id] = Math.max(0, userBizKeys.length - inPeriodSubmittedCount - leaveDaysInPeriod.size);
        timesMap[u.id] = parsedDates;


      } catch (err) {
        console.error(`❌ Error for user ${u.id}:`, err);
        // Safe fallback: recompute userBizKeys here too
        let userStartOnError = start;
        if (u.joiningDate) {
          try {
            const jd = parseISO(u.joiningDate);
            if (!isNaN(jd.getTime()) && isAfter(jd, userStartOnError)) {
              userStartOnError = jd;
            }
          } catch { }
        }
        const userBizKeysOnError = eachDayOfInterval({ start: userStartOnError, end })
          .filter((d) => {
            const dateKey = format(d, "yyyy-MM-dd");
            return !isSunday(d) && !activeHolidays[dateKey];
          })
          .map((d) => format(d, "yyyy-MM-dd"));
        counts[u.id] = userBizKeysOnError.length;
        timesMap[u.id] = [];
      }
    }


    timesheetCache.key = cacheKey;
    timesheetCache.data = {
      missedCount: counts,
      userTimes: timesMap,
    };
    timesheetCache.timestamp = Date.now();

    setMissedCount(counts);
    setUserTimes(timesMap);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    if (selectedMonth > 0) {
      setActiveDate(startOfMonth(new Date(selectedYear, selectedMonth - 1)));
    }
  }, [selectedMonth, selectedYear, calendarYear, calendarUserId, organizationId, users]);

  // sort users alphabetically by name
  const sortedUsers = [...users].sort((a, b) => {
    const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
    const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Filter users based on search query and joining date
  const filteredUsers = sortedUsers.filter((user) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      user.firstName.toLowerCase().includes(searchLower) ||
      user.lastName.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower);

    // Filter out users whose joining date is after the selected period
    if (user.joiningDate) {
      try {
        const joiningDate = parseISO(user.joiningDate);
        // Calculate the period end based on selected month and year
        const periodEnd = selectedMonth === 0
          ? (selectedYear === now.getFullYear() ? now : endOfYear(new Date(selectedYear, 0)))
          : (selectedMonth - 1 === now.getMonth() && selectedYear === now.getFullYear()
            ? now
            : endOfMonth(new Date(selectedYear, selectedMonth - 1)));

        if (!isNaN(joiningDate.getTime()) && isAfter(joiningDate, periodEnd)) {
          return false; // Don't show users who joined after the period
        }
      } catch {
        // If parsing fails, include the user
      }
    }

    return matchesSearch;
  });

  // Helper function to get timesheet submission status for a date
  const getSubmissionStatus = (date: Date, userId: string) => {
    const times = userTimes[userId] || [];
    const submittedSet = new Set(times.map((dt) => dt.toDateString()));
    return submittedSet.has(date.toDateString());
  };

  // Helper function to check if a date is a holiday
  const isHoliday = (date: Date) => {
    const dateKey = format(date, "yyyy-MM-dd");
    return !!holidays[dateKey];
  };

  // Helper function to calculate yearly stats (joining-date + approved leave aware)
  const calculateYearlyStats = (userId: string) => {
    const times = userTimes[userId] || [];
    const user = users.find((u) => u.id === userId);
    const yearStart = startOfYear(new Date(calendarYear, 0));
    const yearEnd =
      calendarYear === now.getFullYear() ? now : endOfYear(yearStart);

    const userJoin = (() => {
      if (!user?.joiningDate) return null;
      try {
        const jd = parseISO(user.joiningDate);
        return isNaN(jd.getTime()) ? null : jd;
      } catch {
        return null;
      }
    })();

    // If selected year is before joining year, or a future year, return zeros
    if (
      (userJoin && calendarYear < userJoin.getFullYear()) ||
      calendarYear > now.getFullYear()
    ) {
      return {
        bizDays: 0,
        submittedDays: 0,
        missedDays: 0,
        submissionRate: 0,
        sundaySubmissions: 0,
        holidaySubmissions: 0,
      };
    }

    // Start from joining date if within the year
    let statsStart = yearStart;
    if (userJoin && isAfter(userJoin, statsStart)) {
      statsStart = userJoin;
    }

    const bizDays = eachDayOfInterval({
      start: statsStart,
      end: yearEnd,
    }).filter((d) => {
      const dateKey = format(d, "yyyy-MM-dd");
      return !isSunday(d) && !holidays[dateKey]; // Exclude Sundays and holidays
    });

    const submittedDays = bizDays.filter((d) => {
      const submittedSet = new Set(times.map((dt) => dt.toDateString()));
      return submittedSet.has(d.toDateString());
    });

    // Count Sunday and holiday timesheet submissions (extra, not part of business days)
    const sundaySubmissions = times.filter((dt) => isSunday(dt));
    const sundaySubmissionsCount = sundaySubmissions.length;

    const holidaySubmissions = times.filter((dt) => {
      const dateKey = format(dt, "yyyy-MM-dd");
      return holidays[dateKey];
    });
    const holidaySubmissionsCount = holidaySubmissions.length;

    // Subtract approved leave days within the year
    const userLeaves = userApprovedLeaves[userId] || [];
    const leaveDaysInYear = new Set<string>();
    userLeaves.forEach((leave: any) => {
      const fromDate = new Date(leave.fromDate);
      const toDate = new Date(leave.toDate);
      const leaveDates = eachDayOfInterval({ start: fromDate, end: toDate });
      leaveDates.forEach((leaveDate) => {
        const key = format(leaveDate, "yyyy-MM-dd");
        if (bizDays.some((d) => format(d, "yyyy-MM-dd") === key)) {
          leaveDaysInYear.add(key);
        }
      });
    });

    const netBizDays = Math.max(0, bizDays.length - leaveDaysInYear.size);
    const missedDays = Math.max(0, netBizDays - submittedDays.length);
    const submissionRate =
      netBizDays > 0
        ? Math.round((submittedDays.length / netBizDays) * 100)
        : 0;

    return {
      bizDays: netBizDays,
      submittedDays: submittedDays.length,
      missedDays,
      submissionRate,
      sundaySubmissions: sundaySubmissionsCount,
      holidaySubmissions: holidaySubmissionsCount,
    };
  };

  // Render year view calendar
  const renderYearCalendar = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    let joinDate: Date | null = null;
    if (user?.joiningDate) {
      try {
        const jd = parseISO(user.joiningDate);
        joinDate = isNaN(jd.getTime()) ? null : jd;
      } catch {
        joinDate = null;
      }
    }
    const months = [];
    for (let month = 0; month < 12; month++) {
      const monthStart = startOfMonth(new Date(calendarYear, month));
      const monthEnd =
        calendarYear === now.getFullYear() && month === now.getMonth()
          ? now
          : endOfMonth(monthStart);

      months.push(
        <div
          key={month}
          className={`month-calendar ${calendarYear === now.getFullYear() && month === now.getMonth()
            ? "current-month"
            : ""
            }`}
        >
          <div className="month-calendar-header">{MONTHS[month]}</div>
          <Calendar
            calendarType="iso8601"
            activeStartDate={monthStart}
            tileClassName={({ date, view }) => {
              if (view !== "month") return "";
              if (date < monthStart || date > monthEnd) return "";
              if (date.getMonth() !== month) return "";
              // Before joining date → normal styling
              if (joinDate && date < joinDate) return "";
              const submitted = getSubmissionStatus(date, userId);
              const holiday = isHoliday(date);

              if (isSunday(date)) {
                return submitted ? "react-calendar__tile--sundaysubmitted" : "";
              }
              if (holiday) {
                return submitted ? "react-calendar__tile--holidaysubmitted" : "react-calendar__tile--holiday";
              }
              if (submitted) {
                return "react-calendar__tile--submitted";
              }
              if (date > now) return "react-calendar__tile--upcoming";
              return "react-calendar__tile--missed";
            }}
            tileContent={({ date, view }) => {
              if (view !== "month") return null;
              if (date.getMonth() !== month) return null;
              const submitted = getSubmissionStatus(date, userId);
              const holiday = isHoliday(date);
              // Holiday: blue square covering the day number
              if (holiday && !submitted) {
                return (
                  <div className="absolute inset-0 flex items-center justify-center z-30">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-500 text-white text-[10px] font-semibold rounded">
                      {date.getDate()}
                    </span>
                  </div>
                );
              }
              // show a light blue circular background for approved leave
              const hasLeave = (userApprovedLeaves[userId] || []).some((leave: any) => {
                const fromDate = new Date(leave.fromDate);
                const toDate = new Date(leave.toDate);
                return date >= fromDate && date <= toDate;
              });
              if (hasLeave) {
                return (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-200 text-blue-800 text-[10px] font-semibold">
                      {date.getDate()}
                    </span>
                  </div>
                );
              }
              return null;
            }}

            showNavigation={false}
          />
        </div>
      );
    }
    return months;
  };

  // Handle view mode change and pass it to parent component
  const handleViewModeChange = (newMode: "CheckIn" | "TimeSheets") => {

    if (newMode === "CheckIn") {
      // hand control back to parent
      onChangeMode?.("CheckIn"); // ⬅️ tell parent to render CheckInAlert
      return;
    }
    setViewMode("TimeSheets"); // stay here when TimeSheets is chosen
  };

  if (isTableMode) {
    return (
      <TimeSheetTable
        organizationId={organizationId}
        users={users}
        onBack={() => setIsTableMode(false)}
        currentUser={currentUser}
        activeTab="TimeSheet"
        onTabChange={(tab) => {
          if (tab === "CheckIn") {
            onChangeMode?.("CheckIn");
          }
        }}
        showBackButton={true}
      />
    );
  }

  // — Calendar view for single user —
  if (calendarUserId) {
    const user = users.find((u) => u.id === calendarUserId)!;

    const stats = calculateYearlyStats(calendarUserId);

    return (
      <div className={`flex flex-col w-full h-full min-h-screen ${isDark ? 'bg-[#0B0B0F] text-white' : 'bg-gray-50 text-black'}`}>
        {/* Enhanced Header */}
        <div className={`${isDark ? 'bg-[#1A1C1F] border-b border-[#2E2F33]' : 'bg-white shadow-sm border-b'}`}>
          <div className="px-6 py-4">
            <div className="flex items-center gap-4 mb-3">
              <button
                onClick={() => {
                  setCalendarUserId(null);
                  setCalendarYear(new Date().getFullYear());
                }}
                className={`flex items-center gap-2 px-3 py-1 border rounded-lg transition-all duration-200 font-medium ${isDark ? 'border-[#B59143] text-[#FBD282] hover:bg-[#2A3441]' : 'border-[#E8C16C] text-[#B59143] hover:bg-[#FFF7E6]'}`}
                aria-label="Go back"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back
              </button>
              <div className="flex items-center gap-3">
                <div className={`${'bg-[#B59143] text-white'} w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm`}>
                  {user.firstName.charAt(0)}
                  {user.lastName.charAt(0)}
                </div>
                <div>
                  <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {user.firstName} {user.lastName}
                  </h1>
                </div>
              </div>
              {/* Year Selection Only */}
              <div className="ml-auto flex items-center gap-3">
                <CustomDropdown
                  value={calendarYear.toString()}
                  onChange={(value) => setCalendarYear(+value)}
                  isDark={isDark}
                  className="px-4 py-2 rounded-lg text-sm font-medium w-32"
                  options={yearOptions.map((year) => ({
                    value: year.toString(),
                    label: year.toString()
                  }))}
                />
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
              <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-4 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-600 text-sm font-medium">
                      Submissions
                    </p>
                    <p className="text-xl font-bold text-green-700">
                      {stats.submittedDays}
                    </p>
                  </div>
                  {/* <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div> */}
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-rose-100 p-4 rounded-lg border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-600 text-sm font-medium">
                      Missed Days
                    </p>
                    <p className="text-xl font-bold text-red-700">
                      {stats.missedDays}
                    </p>
                  </div>
                  {/* <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div> */}
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-600 text-sm font-medium">
                      Business Days
                    </p>
                    <p className="text-xl font-bold text-blue-700">
                      {stats.bizDays}
                    </p>
                  </div>
                  {/* <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <CalendarIcon className="w-4 h-4 text-white" />
                  </div> */}
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-violet-100 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-600 text-sm font-medium">
                      Submission Rate
                    </p>
                    <p className="text-xl font-bold text-purple-700">
                      {stats.submissionRate}%
                    </p>
                  </div>
                  {/* <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                    <div className="text-white text-xs font-bold">%</div>
                  </div> */}
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-yellow-100 p-4 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-600 text-sm font-medium">
                      Holidays Submissions
                    </p>
                    <p className="text-xl font-bold text-amber-700">
                      {stats.sundaySubmissions + stats.holidaySubmissions}
                    </p>
                  </div>
                  {/* <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div> */}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Calendar Section */}
        <div className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <div className={`${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white'} rounded-sm overflow-hidden`}>
              {/* Calendar Header */}
              <div className={`px-6 py-4 ${theme.headerGradient}`}>
                <div className="flex items-center justify-between">
                  <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'}`}>
                    TimeSheet Calendar - {calendarYear}
                  </h2>
                  <div className="flex items-center gap-4 sm:gap-6">
                    {/* Legend - colored chips */}
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="w-3 h-3 bg-red-400 rounded-sm border"></div>
                        <span className={`${isDark ? 'text-white' : 'text-black'} text-xs sm:text-sm font-medium`}>Not submitted</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="w-3 h-3 bg-green-400 rounded-sm border"></div>
                        <span className={`${isDark ? 'text-white' : 'text-black'} text-xs sm:text-sm font-medium`}>Submitted</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="w-3 h-3 bg-amber-400 rounded-sm border"></div>
                        <span className={`${isDark ? 'text-white' : 'text-black'} text-xs sm:text-sm font-medium`}>Holidays Submissions</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="w-3 h-3 bg-blue-500 rounded-sm border"></div>
                        <span className={`${isDark ? 'text-white' : 'text-black'} text-xs sm:text-sm font-medium`}>Holiday</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Calendar Styles */}
              <style>
                {`
                  .react-calendar__tile abbr {
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    width: 1.3rem !important;
                    height: 1.3rem !important;
                    border-radius: 9999px !important;
                  }
                       /* Center tile content to ensure date circle sits in the middle */
                  .react-calendar__tile {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                  }
                  /* Dark/light backgrounds for month tiles */
                  .month-calendar .react-calendar__tile {
                    background: ${isDark ? '#14161B' : '#ffffff'} !important;
                    color: ${isDark ? '#FFFFFF' : '#374151'} !important;
                    border: 1px solid ${isDark ? '#2E2F33' : '#f1f5f9'} !important;
                  }
                  .month-calendar {
                    background: ${isDark ? '#1A1C1F' : '#ffffff'} !important;
                  }
                  .month-calendar .react-calendar__month-view__weekdays {
                    background: ${isDark ? '#14161B' : '#f8fafc'} !important;
                    border-bottom: 1px solid ${isDark ? '#2E2F33' : '#e2e8f0'} !important;
                    color: ${isDark ? '#C3C9D3' : '#64748b'} !important;
                  }
                  .month-calendar-header {
                    background: ${isDark ? '#14161B' : '#f8fafc'} !important;
                    padding: 0.5rem !important;
                    border-bottom: 1px solid ${isDark ? '#2E2F33' : '#e2e8f0'} !important;
                    text-align: center !important;
                    font-weight: 700 !important;
                    color: ${isDark ? '#FFFFFF' : '#111827'} !important;
                    font-size: 0.8rem !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                  }
                  .react-calendar__tile--missed abbr { background: transparent !important; color: #dc2626 !important; }
                  .react-calendar__tile--submitted abbr { background: transparent !important; color: #16a34a !important; }
                  .react-calendar__tile--sundaysubmitted abbr { background: transparent !important; color: #eab308 !important; }
                  .react-calendar__tile--holiday abbr {
                    background: ${isDark ? '#B59143' : '#3b82f6'} !important;
                    color: white !important;
                    border: none !important;
                    border-radius: 50% !important;
                    font-weight: 600 !important;
                    padding: 0 !important;
                    width: 24px !important;
                    height: 24px !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    font-size: 0.8em !important;
                  }
                  
                  .react-calendar__tile--upcoming {
                    background: #f8fafc !important;
                    color: #94a3b8 !important;
                    border: 1px solid #e2e8f0 !important;
                  }
                  .big-calendar .react-calendar__tile--missed,
                  .big-calendar .react-calendar__tile--submitted,
                  .big-calendar .react-calendar__tile--sundaysubmitted {
                    background: ${isDark ? '#14161B' : '#ffffff'} !important;
                    border: 1px solid ${isDark ? '#2E2F33' : '#e5e7eb'} !important;
                    color: inherit !important;
                  }
                  .big-calendar .react-calendar__tile--holiday abbr {
                    background: ${isDark ? '#B59143' : '#3b82f6'} !important;
                    color: white !important;
                    border-radius: 50% !important;
                    font-weight: 600 !important;
                  }
                  .big-calendar .react-calendar__tile--missed:hover,
                  .big-calendar .react-calendar__tile--submitted:hover,
                  .big-calendar .react-calendar__tile--sundaysubmitted:hover,
                  .big-calendar .react-calendar__tile--holiday:hover {
                    background: #f8fafc !important;
                    transform: none !important;
                    box-shadow: none !important;
                  }
                `}
              </style>

              {/* Calendar Body */}
              <div className="p-8">
                <div className="year-calendar">
                  {renderYearCalendar(calendarUserId)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // — Default list view —
  return (
    <div className="flex flex-col w-full">
      <div className={`w-full px-8 py-4 rounded-lg mb-2 ${theme.headerGradient} border ${theme.border}`}>
        {isDark && (
          <>
            <span className={theme.headerOverlayGlow} />
            <span className={theme.headerOverlayTopLine} />
            <span className={theme.headerOverlayBottomGlow} />
            <span className={theme.headerOverlaySweep} />
          </>
        )}
        <h1 className="text-2xl font-bold">TimeSheet Alerts</h1>
        <p className={`text-xs ${theme.textMuted}`}>
          For each user, the badge shows how many business days they missed submitting timesheets this month. Sundays are now tracked separately.
        </p>
      </div>

      {/* single container with outer border */}
      <div className={`px-8 mt-0 rounded-md overflow-hidden ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white border border-gray-200'}`}>
        {/* dropdown section with its own bottom border - ADDED VIEW MODE DROPDOWN */}
        <div className={`flex py-3 items-center gap-4 px-4 ${theme.card} border-b ${theme.border} rounded-t-md`}>
          <CustomDropdown
            value={viewMode}
            onChange={(value) =>
              handleViewModeChange(value as "CheckIn" | "TimeSheets")
            }
            isDark={isDark}
            className="w-40 text-sm rounded px-3 py-2"
            options={[
              { value: "CheckIn", label: "CheckIn" },
              { value: "TimeSheets", label: "TimeSheets" }
            ]}
          />
          <CustomDropdown
            value={selectedMonth.toString()}
            onChange={(value) => setSelectedMonth(+value)}
            isDark={isDark}
            className="w-40 text-sm rounded px-3 py-2"
            options={[
              { value: "0", label: "All" },
              ...MONTHS.map((m, i) => ({
                value: (i + 1).toString(),
                label: m
              })).filter((option) => {
                // If current year is selected, only show months up to current month
                if (selectedYear === now.getFullYear()) {
                  const monthIndex = parseInt(option.value) - 1;
                  return monthIndex <= now.getMonth();
                }
                return true; // Show all months for past years
              })
            ]}
          />
          <CustomDropdown
            value={selectedYear.toString()}
            onChange={(value) => setSelectedYear(+value)}
            isDark={isDark}
            className="w-32 text-sm rounded px-3 py-2"
            options={yearOptions.map((year) => ({
              value: year.toString(),
              label: year.toString()
            }))}
          />
          <button
            onClick={() => fetchData(true)}
            className={`p-2.5 rounded transition-colors ${isDark
              ? "bg-[#2E2F33] hover:bg-[#3E4045] text-gray-300 border border-[#444]"
              : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            title="Refresh Data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => setIsTableMode(true)}
            className={`ml-auto p-2 rounded-lg transition-colors ${isDark ? "hover:bg-[#2A3441] text-gray-400 hover:text-white" : "hover:bg-gray-100 text-gray-500 hover:text-gray-900"
              }`}
            title="Table View"
          >
            <Table size={20} />
          </button>
        </div>

        {/* Search and Member Count Section */}
        <div className={`flex justify-between items-center py-3 px-4 ${theme.card} border-b ${theme.border}`}>
          <div className={`relative flex items-center max-w-sm w-full rounded-full ${theme.cardMuted} overflow-hidden`}>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`${theme.searchInput} block w-full pl-10 pr-3 py-1.5 border rounded-full leading-5 focus:outline-none sm:text-sm`}
            />
          </div>
          <div className="text-sm text-gray-500">
            {filteredUsers.length} members
          </div>
        </div>

        {loading ? (
          <div className="py-0 overflow-auto">
            <table className={`min-w-full divide-y text-sm ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
              <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-4 py-2 text-left text-sm font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                    Name
                  </th>
                  <th className={`px-4 py-2 text-left text-sm font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                    Email
                  </th>
                  <th className={`px-4 py-2 text-center text-sm font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                    Missed Days
                  </th>
                </tr>
              </thead>
              <tbody className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                {Array.from({ length: 6 }).map((_, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 text-sm">
                      <Skeleton
                        height="16px"
                        width="120px"
                        startColor={isDark ? "gray.700" : "gray.200"}
                        endColor={isDark ? "gray.600" : "gray.300"}
                        borderRadius="md"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Skeleton
                        height="16px"
                        width="180px"
                        startColor={isDark ? "gray.700" : "gray.200"}
                        endColor={isDark ? "gray.600" : "gray.300"}
                        borderRadius="md"
                      />
                    </td>
                    <td className="px-4 py-3 text-center flex justify-center">
                      <Skeleton
                        height="20px"
                        width="32px"
                        startColor={isDark ? "gray.700" : "gray.200"}
                        endColor={isDark ? "gray.600" : "gray.300"}
                        borderRadius="full"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-0 overflow-auto" style={{ maxHeight: "62vh" }}>
            <table className={`min-w-full divide-y text-sm ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
              <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-4 py-2 text-left text-sm font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                    Name
                  </th>
                  <th className={`px-4 py-2 text-left text-sm font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                    Email
                  </th>
                  <th className={`px-4 py-2 text-center text-sm font-semibold uppercase tracking-wider ${isDark ? 'bg-[#14161B] text-white' : 'bg-gray-50 text-gray-600'}`}>
                    Missed Days
                  </th>
                </tr>
              </thead>
              <tbody className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-200'}`}>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className={`text-center py-10 font-normal ${isDark ? 'text-[#C3C9D3]' : 'text-gray-500'}`}
                    >
                      {searchQuery
                        ? "No members found matching your search."
                        : "No members found."}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => (
                    <tr
                      key={u.id}
                      className={`cursor-pointer transition-colors ${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#FFF7E6]'}`}
                      onClick={() => {
                        setCalendarYear(new Date().getFullYear());
                        setCalendarUserId(u.id);
                      }}
                    >
                      <td className="px-4 py-3 text-sm">
                        {u.firstName} {u.lastName}
                      </td>
                      <td className="px-4 py-3 text-sm">{u.email}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {missedCount[u.id] ?? 0}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

          </div>
        )}
      </div>
    </div>
  );
};

export default TimeSheetAlert;
